import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { ProgressSteps } from '@/components/sandbox/ProgressSteps';
import * as sandboxApi from '@/api/sandbox';
import type { SandboxStatus, SandboxProgress } from '@/api/sandbox';

const STATUS_MESSAGES: Record<SandboxStatus, string> = {
  none: 'Initializing sandbox...',
  pending: 'Sandbox creation requested...',
  creating: 'Creating your sandbox...',
  ready: 'Sandbox is ready!',
  failed: 'Sandbox creation failed',
  restarting: 'Restarting sandbox...',
  deleting: 'Sandbox is being deleted...',
};

const MAX_POLL_ATTEMPTS = 90; // 3 minutes at 2s intervals
const POLL_INTERVAL = 2000;

export default function SandboxLoadingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [status, setStatus] = useState<SandboxStatus>('none');
  const [sandboxUrl, setSandboxUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [progress, setProgress] = useState<SandboxProgress | undefined>();

  const initializeSandbox = useCallback(async () => {
    if (!user?.username) return;

    try {
      setError(null);
      const response = await sandboxApi.createSandbox();
      setStatus(response.status);
      setSandboxUrl(response.sandboxUrl);

      if (response.status === 'ready') {
        // Sandbox is already ready, redirect immediately
        window.location.href = response.sandboxUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create sandbox');
    }
  }, [user?.username]);

  const pollStatus = useCallback(async () => {
    try {
      const response = await sandboxApi.getSandboxStatus();
      setStatus(response.status);
      setSandboxUrl(response.sandboxUrl);
      setProgress(response.progress);

      if (response.status === 'ready') {
        // Sandbox is ready, redirect
        window.location.href = response.sandboxUrl;
        return true;
      }

      if (response.status === 'failed') {
        setError(response.error || 'Sandbox creation failed');
        return true;
      }

      return false;
    } catch (err) {
      // Don't set error on polling failures, just continue
      console.error('Failed to poll sandbox status:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    initializeSandbox();
  }, [isAuthenticated, navigate, initializeSandbox]);

  useEffect(() => {
    if (status === 'ready' || status === 'failed' || error) {
      return;
    }

    if (pollCount >= MAX_POLL_ATTEMPTS) {
      setError('Sandbox creation timed out. Please try again.');
      return;
    }

    const timer = setTimeout(async () => {
      const done = await pollStatus();
      if (!done) {
        setPollCount((c) => c + 1);
      }
    }, POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [status, pollCount, error, pollStatus]);

  const handleRetry = () => {
    setError(null);
    setPollCount(0);
    initializeSandbox();
  };

  const handleGoBack = () => {
    navigate('/settings');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-md-cream">
      <Card className="w-full max-w-md p-8 text-center">
        {error ? (
          <>
            <div className="text-red-500 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-md-graphite mb-2">
              Something went wrong
            </h2>
            <p className="text-md-ink mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <Button variant="secondary" onClick={handleGoBack}>
                Go Back
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Retry
              </Button>
            </div>
          </>
        ) : (
          <>
            <img
              src={`${import.meta.env.BASE_URL}bunny-loading.png`}
              alt="Loading bunny"
              className="w-32 h-32 mx-auto mb-6 animate-bounce"
            />
            <h2 className="text-xl font-bold text-md-graphite mb-6 uppercase tracking-wider">
              {STATUS_MESSAGES[status]}
            </h2>

            {/* Progress Steps */}
            {(status === 'creating' || status === 'pending') && (
              <div className="mb-6">
                <ProgressSteps progress={progress} />
              </div>
            )}

            <p className="text-md-ink text-sm">
              {status === 'creating' || status === 'pending'
                ? 'This may take a moment. Please wait...'
                : 'Please wait...'}
            </p>
            {sandboxUrl && (
              <p className="text-md-slate text-xs mt-4">
                Sandbox URL: {sandboxUrl}
              </p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
